import React, { useState } from 'react';
import { Modal, InputNumber } from 'antd';
import { Bounce, toast } from 'react-toastify';
import useAuthRequest from '../../../services/requests.service';

const r = (envKey, fallback) => parseFloat(process.env[envKey]) || fallback;

const PRICE_PERMANENT = process.env.REACT_APP_PRICE_PERMANENT;
const SEASONAL_TIERS = [
  { min: 200, priceId: process.env.REACT_APP_PRICE_SEASONAL_200,  rate: r('REACT_APP_RATE_SEASONAL_200',  2.50) },
  { min: 100, priceId: process.env.REACT_APP_PRICE_SEASONAL_100,  rate: r('REACT_APP_RATE_SEASONAL_100',  2.70) },
  { min: 50,  priceId: process.env.REACT_APP_PRICE_SEASONAL_50,   rate: r('REACT_APP_RATE_SEASONAL_50',   2.80) },
  { min: 20,  priceId: process.env.REACT_APP_PRICE_SEASONAL_20,   rate: r('REACT_APP_RATE_SEASONAL_20',   2.90) },
  { min: 1,   priceId: process.env.REACT_APP_PRICE_SEASONAL_BASE, rate: r('REACT_APP_RATE_SEASONAL_BASE', 3.00) },
];

const getSeasonalTier = (qty) => SEASONAL_TIERS.find((t) => qty >= t.min);

const NewPlanModal = ({ page, onClose, hasPermamentPlan }) => {
  const request = useAuthRequest();
  const [planType, setPlanType] = useState(hasPermamentPlan ? 'seasonal' : 'permanent');
  const [qty, setQty] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const permanentRate = r('REACT_APP_RATE_PERMANENT', 3.00);
  const seasonalTier = getSeasonalTier(qty);
  const pricePerSeat = planType === 'permanent' ? permanentRate : seasonalTier.rate;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const priceId = planType === 'permanent' ? PRICE_PERMANENT : getSeasonalTier(qty).priceId;
      const res = await request.postDataAPI('stripe/checkout-link', {
        company: page._id,
        email: page.email,
        name: page.name,
        customerId: page.customerId,
        priceId,
        qty,
        planType,
        accountId: page._id,
      });
      if (res.session?.url) {
        window.location.href = res.session.url;
      } else {
        toast.error('Impossible de générer le lien de paiement', {
          position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
        });
      }
    } catch (e) {
      console.error('Checkout failed', e);
      toast.error('Une erreur est survenue lors de la création du paiement', {
        position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={true}
      title="Créer un nouvel abonnement"
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Continuer vers le paiement →"
      confirmLoading={isLoading}
      centered
    >
      <div className="flex flex-col gap-4 py-2">
        {/* Sélection du type */}
        <div>
          <div className="text-sm font-semibold text-slate-600 mb-2">Type d'abonnement</div>
          <div className="flex gap-3">
            <div
              onClick={() => !hasPermamentPlan && setPlanType('permanent')}
              className={`flex-1 border-2 rounded-lg p-4 transition-all ${
                hasPermamentPlan
                  ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                  : planType === 'permanent'
                  ? 'border-royalblue-500 bg-royalblue-50 cursor-pointer'
                  : 'border-slate-200 hover:border-slate-300 cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">♾️</span>
                <span className="font-bold text-royalblue-500 text-sm">Permanent</span>
                {hasPermamentPlan && <span className="text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full ml-auto">Déjà souscrit</span>}
              </div>
              <div className="text-lg font-bold text-slate-800">{permanentRate.toFixed(2).replace('.', ',')}€ <span className="text-xs font-normal text-slate-400">/siège/mois</span></div>
              <ul className="mt-2 space-y-1">
                <li className="text-xs text-slate-500 flex items-start gap-1"><span className="text-green-500 mt-0.5">✓</span> Renouvellement automatique</li>
                <li className="text-xs text-slate-500 flex items-start gap-1"><span className="text-green-500 mt-0.5">✓</span> Résiliable après 3 mois</li>
                <li className="text-xs text-slate-500 flex items-start gap-1"><span className="text-green-500 mt-0.5">✓</span> Ajout de sièges à tout moment</li>
              </ul>
            </div>
            <div
              onClick={() => setPlanType('seasonal')}
              className={`flex-1 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                planType === 'seasonal'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">🌿</span>
                <span className="font-bold text-amber-500 text-sm">Saisonnier</span>
              </div>
              <div className="text-lg font-bold text-slate-800">
                {getSeasonalTier(qty).rate}€ <span className="text-xs font-normal text-slate-400">/siège/mois</span>
              </div>
              {qty >= 20 && (
                <div className="text-xs text-amber-600 bg-amber-100 rounded px-2 py-0.5 inline-block mt-1">
                  Tarif pack {qty >= 200 ? '200' : qty >= 100 ? '100' : qty >= 50 ? '50' : '20'} sièges
                </div>
              )}
              <ul className="mt-2 space-y-1">
                <li className="text-xs text-slate-500 flex items-start gap-1"><span className="text-amber-400 mt-0.5">!</span> Durée fixe de 3 mois</li>
                <li className="text-xs text-slate-500 flex items-start gap-1"><span className="text-amber-400 mt-0.5">!</span> Non résiliable</li>
                <li className="text-xs text-slate-500 flex items-start gap-1"><span className="text-green-500 mt-0.5">✓</span> Expire automatiquement</li>
              </ul>
              {planType === 'seasonal' && (
                <div className="mt-3 flex gap-1 flex-wrap">
                  {[20, 50, 100, 200].map((pack) => (
                    <button
                      key={pack}
                      onClick={(e) => { e.stopPropagation(); setQty(pack); }}
                      className={`text-xs px-2 py-1 rounded border transition-all ${
                        qty === pack
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-white text-amber-500 border-amber-300 hover:bg-amber-50'
                      }`}
                    >
                      {pack} sièges
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quantité */}
        <div>
          <div className="text-sm font-semibold text-slate-600 mb-2">Nombre de sièges</div>
          <InputNumber
            min={1}
            value={qty}
            onChange={(v) => setQty(v ?? 1)}
            className="w-full"
          />
          <div className="text-xs text-slate-400 mt-1">
            Prix estimé : {pricePerSeat}€ × {qty} = <strong>{(pricePerSeat * qty).toFixed(2)}€/mois</strong>
            {planType === 'seasonal' && <span className="ml-1">(× 3 mois = <strong>{(pricePerSeat * qty * 3).toFixed(2)}€</strong>)</span>}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NewPlanModal;
